export interface RouteMatch {
    readonly params: Readonly<Record<string, string>>;
    /** The part of the path this route consumed, so nested routers can continue from there. */
    readonly consumed: string;
    readonly remaining: string;
}

export interface CompiledPattern {
    readonly source: string;

    match(path: string): RouteMatch | undefined;
}

/**
 * Compiles `/products/:code` style patterns. A trailing `/*` consumes only its prefix and
 * leaves the rest for a nested router.
 */
export const compilePattern = (pattern: string | RegExp): CompiledPattern => {
    if (pattern instanceof RegExp) {
        return {
            source: pattern.source,
            match: path => {
                const result = pattern.exec(path);
                if (!result) {
                    return undefined;
                }
                const consumed = result[0];
                return {
                    params: {...result.groups},
                    consumed,
                    remaining: path.slice(consumed.length)
                };
            }
        };
    }

    const partial = pattern.endsWith("/*");
    const body = partial ? pattern.slice(0, -2) : pattern;
    const names: string[] = [];
    const source = body
        .split("/")
        .map(segment => {
            if (!segment.startsWith(":")) {
                return escapeRegExp(segment);
            }
            names.push(segment.slice(1));
            return "([^/]+)";
        })
        .join("/");
    const expression = new RegExp(`^${source}${partial ? "(?=/|$)" : "/?$"}`);

    return {
        source: pattern,
        match: path => {
            const result = expression.exec(path);
            if (!result) {
                return undefined;
            }
            const params: Record<string, string> = {};
            names.forEach((name, index) => {
                const value = result[index + 1];
                if (value !== undefined) {
                    params[name] = decodeURIComponent(value);
                }
            });
            const consumed = result[0];
            return {params, consumed, remaining: path.slice(consumed.length)};
        }
    };
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
